import React, { Suspense, lazy, useEffect, useState, useCallback } from 'react';
import { createBrowserRouter, Outlet, RouterProvider, useLocation, Link } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { CurrencyProvider } from './context/CurrencyContext';
import CurrencyToggle from './components/CurrencyToggle';
import NewsletterSignup from './components/NewsletterSignup';
import TestModeBanner from './components/TestModeBanner';
import { captureReferralFromUrl } from './utils/referral';
import { isTestMode } from './utils/network';
import Home from './pages/Home';
import './App.css';

// Lazy-load heavier pages for better initial load
const Challenge = lazy(() => import('./pages/Challenge'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const Admin2FASetup = lazy(() => import('./pages/Admin2FASetup'));
const MetaMaskGuide = lazy(() => import('./pages/MetaMaskGuide'));
const About = lazy(() => import('./pages/About'));
const Blog = lazy(() => import('./pages/Blog'));
const Press = lazy(() => import('./pages/Press'));
const SkillBasedWagering = lazy(() => import('./pages/SkillBasedWagering'));
const LegacyGameRedirect = lazy(() => import('./pages/LegacyGameRedirect'));
const ChallengesList = lazy(() => import('./pages/ChallengesList'));
const Spotlight = lazy(() => import('./pages/Spotlight'));
const TransactionsLedger = lazy(() => import('./pages/TransactionsLedger'));

const SITE_NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/blog', label: 'Blog' },
  { to: '/skill-based-wagering', label: 'Skill wagering' },
  { to: '/press', label: 'Press' },
  { to: '/spotlight', label: 'Spotlight' },
];

function PageLoader() {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: '#888', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span>Loading…</span>
    </div>
  );
}

// Route debugger component (no logging in production)
function RouteDebugger() {
  const location = useLocation();
  return null;
}

function ReferralCapture() {
  const location = useLocation();
  useEffect(() => {
    captureReferralFromUrl();
  }, [location.search]);
  return null;
}

function RootLayout() {
  const location = useLocation();
  const hideSiteFooter = location.pathname.startsWith('/admin');
  const testBanner = isTestMode();
  const [navOpen, setNavOpen] = useState(false);

  const closeNav = useCallback(() => setNavOpen(false), []);

  useEffect(() => {
    closeNav();
  }, [location.pathname, closeNav]);

  useEffect(() => {
    const bottom = testBanner ? '96px' : '52px';
    document.documentElement.style.setProperty('--app-header-bottom', bottom);
    return () => document.documentElement.style.removeProperty('--app-header-bottom');
  }, [testBanner]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeNav();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [navOpen, closeNav]);

  return (
    <>
      <ReferralCapture />
      <RouteDebugger />
      <TestModeBanner />
      <div className={`app-header-bar${testBanner ? ' app-header-bar--test-banner' : ''}`}>
        <div className={`app-header-bar-inner${navOpen ? ' app-header-bar-inner--nav-open' : ''}`}>
          <button
            type="button"
            className="app-nav-menu-btn"
            onClick={() => setNavOpen((o) => !o)}
            aria-expanded={navOpen}
            aria-controls="primary-site-nav"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
          >
            <span className={`app-nav-menu-icon${navOpen ? ' app-nav-menu-icon--open' : ''}`} aria-hidden>
              <span className="app-nav-menu-bar" />
              <span className="app-nav-menu-bar" />
              <span className="app-nav-menu-bar" />
            </span>
          </button>
          <nav id="primary-site-nav" className={`app-header-nav${navOpen ? ' app-header-nav--open' : ''}`} aria-label="Main">
            {SITE_NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} className="app-nav-link" onClick={closeNav}>
                {label}
              </Link>
            ))}
          </nav>
          <span className="app-header-bar-spacer" />
          <div className="app-header-actions">
            <CurrencyToggle />
          </div>
        </div>
      </div>
      {navOpen ? (
        <button
          type="button"
          className="app-nav-backdrop"
          aria-label="Close menu"
          onClick={closeNav}
        />
      ) : null}
      <main
        className="app-main"
        style={{ paddingTop: testBanner ? 88 : 52 }}
      >
        <Outlet />
      </main>
      {!hideSiteFooter ? (
        <footer className="app-site-footer">
          <div className="app-site-footer-inner">
            <NewsletterSignup source="site-footer" />
          </div>
        </footer>
      ) : null}
    </>
  );
}

function NotFound() {
  const location = useLocation();
  return (
    <div style={{ padding: '20px', color: '#fff', background: '#141414', minHeight: '100vh' }}>
      404 - Page not found: {location.pathname}
    </div>
  );
}

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <RootLayout />,
      children: [
        { index: true, element: <Home /> },
        { path: 'create-challenge/:gameSlug', element: <Home /> },
        {
          path: 'game/:gameId',
          element: (
            <Suspense fallback={<PageLoader />}>
              <LegacyGameRedirect />
            </Suspense>
          ),
        },
        { path: 'challenge/:id', element: <Suspense fallback={<PageLoader />}><Challenge /></Suspense> },
        { path: 'challenges', element: <Suspense fallback={<PageLoader />}><ChallengesList /></Suspense> },
        { path: 'spotlight', element: <Suspense fallback={<PageLoader />}><Spotlight /></Suspense> },
        { path: 'transactions', element: <Suspense fallback={<PageLoader />}><TransactionsLedger /></Suspense> },
        { path: 'about', element: <Suspense fallback={<PageLoader />}><About /></Suspense> },
        { path: 'blog', element: <Suspense fallback={<PageLoader />}><Blog /></Suspense> },
        { path: 'blog/:slug', element: <Suspense fallback={<PageLoader />}><Blog /></Suspense> },
        { path: 'press', element: <Suspense fallback={<PageLoader />}><Press /></Suspense> },
        {
          path: 'skill-based-wagering',
          element: (
            <Suspense fallback={<PageLoader />}>
              <SkillBasedWagering />
            </Suspense>
          ),
        },
        { path: 'metamask-guide', element: <Suspense fallback={<PageLoader />}><MetaMaskGuide /></Suspense> },
        { path: 'admin/login', element: <Suspense fallback={<PageLoader />}><AdminLogin /></Suspense> },
        { path: 'admin/settings', element: <Suspense fallback={<PageLoader />}><AdminSettings /></Suspense> },
        { path: 'admin/2fa/setup', element: <Suspense fallback={<PageLoader />}><Admin2FASetup /></Suspense> },
        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
);

function App() {
  return (
    <CurrencyProvider>
      <RouterProvider router={router} />
      <Analytics />
    </CurrencyProvider>
  );
}

export default App;
